import React, { useState, useEffect } from 'react';
import { dealerSalesRepsApi } from '@/services/api';
import api from '@/services/api';
import { DealerSalesRep, CreateDealerSalesRepDto, UpdateDealerSalesRepDto } from '@/types';
import { UserFormModal } from '@/components/admin/UserFormModal';

const roleColors: Record<string, string> = {
  sales_rep: 'bg-blue-100 text-blue-800',
  sales_manager: 'bg-purple-100 text-purple-800',
};

const roleLabels: Record<string, string> = {
  sales_rep: 'Sales Rep',
  sales_manager: 'Sales Manager',
};

const statusColors: Record<string, string> = {
  active: 'bg-green-100 text-green-800',
  inactive: 'bg-gray-100 text-gray-800',
  terminated: 'bg-red-100 text-red-800',
};

const statusLabels: Record<string, string> = {
  active: 'Active',
  inactive: 'Inactive',
  terminated: 'Terminated',
};

export function TeamManagementPage() {
  const [salesReps, setSalesReps] = useState<DealerSalesRep[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filter state
  const [roleFilter, setRoleFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('active');
  const [searchQuery, setSearchQuery] = useState('');

  // Pagination state
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalReps, setTotalReps] = useState(0);
  const [pageSize] = useState(20);

  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingRep, setEditingRep] = useState<DealerSalesRep | null>(null);
  const [isResetPasswordModalOpen, setIsResetPasswordModalOpen] = useState(false);
  const [resetPasswordRep, setResetPasswordRep] = useState<DealerSalesRep | null>(null);

  // Debounced search
  const [debouncedSearch, setDebouncedSearch] = useState('');

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setPage(1);
  }, [roleFilter, statusFilter, debouncedSearch]);

  // Fetch sales reps
  useEffect(() => {
    fetchSalesReps();
  }, [roleFilter, statusFilter, debouncedSearch, page]);

  const fetchSalesReps = async () => {
    try {
      setLoading(true);
      setError(null);

      const params: any = {
        page,
        limit: pageSize,
      };

      if (roleFilter !== 'all') {
        params.role = roleFilter;
      }

      if (statusFilter !== 'all') {
        params.status = statusFilter;
      }

      if (debouncedSearch) {
        params.search = debouncedSearch;
      }

      const response = await dealerSalesRepsApi.list(params);
      setSalesReps(response?.salesReps || []);
      setTotalPages(response?.pagination?.pages || 1);
      setTotalReps(response?.pagination?.total || 0);
    } catch (err: any) {
      setError(err.message || 'Failed to load sales team');
    } finally {
      setLoading(false);
    }
  };

  const clearFilters = () => {
    setRoleFilter('all');
    setStatusFilter('active');
    setSearchQuery('');
  };

  const hasActiveFilters = roleFilter !== 'all' || statusFilter !== 'active' || searchQuery !== '';

  const handleAdd = () => {
    setEditingRep(null);
    setIsModalOpen(true);
  };

  const handleEdit = (rep: DealerSalesRep) => {
    setEditingRep(rep);
    setIsModalOpen(true);
  };

  const handleDeactivate = async (rep: DealerSalesRep) => {
    if (!confirm(`Are you sure you want to deactivate ${rep.firstName} ${rep.lastName}?`)) {
      return;
    }

    try {
      await dealerSalesRepsApi.deactivate(rep.id);
      fetchSalesReps();
    } catch (err: any) {
      alert(`Failed to deactivate: ${err.message}`);
    }
  };

  const handleResetPassword = (rep: DealerSalesRep) => {
    setResetPasswordRep(rep);
    setIsResetPasswordModalOpen(true);
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
    }).format(amount || 0);
  };

  const formatDate = (dateString: string | undefined) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const [activeTab, setActiveTab] = useState<'team' | 'portal'>('team');
  const [portalUsers, setPortalUsers] = useState<any[]>([]);
  const [portalLoading, setPortalLoading] = useState(false);
  const [showAddPortalUser, setShowAddPortalUser] = useState(false);
  const [portalForm, setPortalForm] = useState({ firstName: '', lastName: '', email: '', password: '', phone: '', isAdmin: false });
  const [portalSaving, setPortalSaving] = useState(false);
  const [portalGenPassword, setPortalGenPassword] = useState<string | null>(null);

  const loadPortalUsers = async () => {
    setPortalLoading(true);
    try {
      const res = await api.client.get('/dealers/my/portal-users');
      setPortalUsers(res.data || []);
    } catch (e) { console.error(e); }
    finally { setPortalLoading(false); }
  };

  useEffect(() => { if (activeTab === 'portal') loadPortalUsers(); }, [activeTab]);

  const handleAddPortalUser = async () => {
    if (!portalForm.firstName || !portalForm.email || !portalForm.password) return;
    setPortalSaving(true);
    try {
      await api.client.post('/dealers/my/portal-users', portalForm);
      setShowAddPortalUser(false);
      setPortalForm({ firstName: '', lastName: '', email: '', password: '', phone: '', isAdmin: false });
      loadPortalUsers();
    } catch (err: any) {
      alert(err.response?.data?.message || err.message || 'Failed to add user');
    } finally { setPortalSaving(false); }
  };

  const handleDeactivatePortalUser = async (contactId: string, name: string) => {
    if (!confirm(`Deactivate ${name}?`)) return;
    try {
      await api.client.post(`/dealers/my/portal-users/${contactId}/deactivate`);
      loadPortalUsers();
    } catch (err: any) { alert(err.response?.data?.message || 'Failed'); }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Team Management</h1>
          <p className="text-gray-400 mt-1">Manage your sales team and portal access</p>
        </div>
        {activeTab === 'team' ? (
          <button
            onClick={handleAdd}
            className="bg-yellow-500 hover:bg-yellow-600 text-black font-semibold px-6 py-2 rounded-lg transition-colors"
          >
            + Add Team Member
          </button>
        ) : (
          <button
            onClick={() => setShowAddPortalUser(true)}
            className="bg-yellow-500 hover:bg-yellow-600 text-black font-semibold px-6 py-2 rounded-lg transition-colors"
          >
            + Add Portal User
          </button>
        )}
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-[var(--bg-card)] rounded-lg p-1 w-fit border border-[var(--border-color)]">
        <button onClick={() => setActiveTab('team')} className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${activeTab === 'team' ? 'bg-yellow-500 text-black' : 'text-gray-400 hover:text-white'}`}>
          Sales Team
        </button>
        <button onClick={() => setActiveTab('portal')} className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${activeTab === 'portal' ? 'bg-yellow-500 text-black' : 'text-gray-400 hover:text-white'}`}>
          Portal Users
        </button>
      </div>

      {/* Portal Users Tab */}
      {activeTab === 'portal' && (
        <div className="card">
          {portalLoading ? (
            <p className="text-gray-400 text-center py-8">Loading...</p>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-gray-500 uppercase border-b border-[var(--border-color)]">
                  <th className="px-4 py-3">Name</th>
                  <th className="px-4 py-3">Email</th>
                  <th className="px-4 py-3">Role</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[var(--border-color)]">
                {portalUsers.map((u: any) => (
                  <tr key={u.id}>
                    <td className="px-4 py-3 text-white font-medium">
                      {u.first_name} {u.last_name}
                      {u.is_primary && <span className="ml-2 text-xs text-green-400">(Primary)</span>}
                    </td>
                    <td className="px-4 py-3 text-gray-400">{u.email}</td>
                    <td className="px-4 py-3">
                      <span className={`text-xs px-2 py-0.5 rounded-full ${u.is_admin ? 'bg-purple-500/20 text-purple-400' : 'bg-gray-500/20 text-gray-400'}`}>
                        {u.is_admin ? 'Admin' : 'Standard'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-xs px-2 py-0.5 rounded-full ${u.status === 'active' ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>
                        {u.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      {u.status === 'active' && !u.is_primary && (
                        <button
                          onClick={() => handleDeactivatePortalUser(u.id, `${u.first_name} ${u.last_name}`)}
                          className="text-xs text-red-400 hover:text-red-300"
                        >
                          Deactivate
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
                {portalUsers.length === 0 && (
                  <tr><td colSpan={5} className="px-4 py-8 text-center text-gray-500">No portal users</td></tr>
                )}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Add Portal User Modal */}
      {showAddPortalUser && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-[var(--bg-card)] border border-[var(--border-color)] rounded-lg p-6 max-w-md w-full">
            <h3 className="text-xl font-bold text-white mb-4">Add Portal User</h3>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-gray-400 mb-1">First Name *</label>
                  <input type="text" value={portalForm.firstName} onChange={e => setPortalForm({...portalForm, firstName: e.target.value})} className="input-field" />
                </div>
                <div>
                  <label className="block text-xs text-gray-400 mb-1">Last Name *</label>
                  <input type="text" value={portalForm.lastName} onChange={e => setPortalForm({...portalForm, lastName: e.target.value})} className="input-field" />
                </div>
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-1">Email *</label>
                <input type="email" value={portalForm.email} onChange={e => setPortalForm({...portalForm, email: e.target.value})} className="input-field" />
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-1">Password *</label>
                <input type="password" value={portalForm.password} onChange={e => setPortalForm({...portalForm, password: e.target.value})} className="input-field" placeholder="Min 8 characters" />
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-1">Phone</label>
                <input type="tel" value={portalForm.phone} onChange={e => setPortalForm({...portalForm, phone: e.target.value})} className="input-field" />
              </div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={portalForm.isAdmin} onChange={e => setPortalForm({...portalForm, isAdmin: e.target.checked})} className="rounded" />
                <span className="text-sm text-gray-300">Admin access (can manage team, view all orders)</span>
              </label>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button onClick={() => setShowAddPortalUser(false)} className="px-4 py-2 text-gray-400 hover:text-white">Cancel</button>
              <button onClick={handleAddPortalUser} disabled={portalSaving || !portalForm.firstName || !portalForm.email || !portalForm.password}
                className="px-4 py-2 bg-yellow-500 text-black font-semibold rounded-lg disabled:opacity-50">
                {portalSaving ? 'Adding...' : 'Add User'}
              </button>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'team' && <></>}
      {activeTab !== 'team' ? null : (
      <></>
      )}

      {activeTab === 'team' && (<>
      {/* existing sales team content below */}

      {/* Search & Filters */}
      <div className="card">
        <div className="space-y-4">
          {/* Search Bar */}
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
            <input
              type="text"
              placeholder="Search by name or email..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="input-field pl-10"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-white"
              >
                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>

          {/* Filter Row */}
          <div className="flex flex-wrap items-center gap-4">
            {/* Role Filter */}
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-gray-400">Role:</label>
              <select
                value={roleFilter}
                onChange={(e) => setRoleFilter(e.target.value)}
                className="select-field py-2 px-3 text-sm"
              >
                <option value="all">All Roles</option>
                {Object.entries(roleLabels).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            </div>

            {/* Status Filter */}
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-gray-400">Status:</label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="select-field py-2 px-3 text-sm"
              >
                <option value="all">All Statuses</option>
                {Object.entries(statusLabels).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            </div>

            {/* Clear Filters */}
            {hasActiveFilters && (
              <button
                onClick={clearFilters}
                className="text-sm text-keder-yellow hover:text-yellow-400 font-medium flex items-center gap-1"
              >
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
                Clear Filters
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Results Count */}
      {!loading && (
        <div className="text-sm text-gray-400">
          Showing {(salesReps || []).length} of {totalReps} team members
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="card bg-red-900/20 border-red-800">
          <p className="text-red-400">{error}</p>
        </div>
      )}

      {/* Loading State */}
      {loading ? (
        <div className="card">
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-2 border-keder-gray border-t-keder-yellow"></div>
          </div>
        </div>
      ) : (salesReps || []).length === 0 ? (
        /* Empty State */
        <div className="card text-center py-12">
          <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
          </svg>
          <h3 className="mt-2 text-sm font-medium text-white">No team members found</h3>
          <p className="mt-1 text-sm text-gray-400">
            {hasActiveFilters ? 'Try adjusting your filters.' : 'Get started by adding your first team member.'}
          </p>
          {!hasActiveFilters && (
            <button
              onClick={handleAdd}
              className="mt-4 bg-yellow-500 hover:bg-yellow-600 text-black font-semibold px-6 py-2 rounded-lg transition-colors"
            >
              + Add Team Member
            </button>
          )}
        </div>
      ) : (
        /* Team Table */
        <div className="card overflow-hidden p-0">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-keder-gray">
              <thead className="bg-keder-gray/50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                    Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                    Email
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                    Role
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                    Comm Rate
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                    Orders
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                    Hired
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-keder-gray/50">
                {(salesReps || []).map((rep) => (
                  <tr key={rep.id} className="hover:bg-keder-gray/20">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10 rounded-full bg-keder-yellow text-black flex items-center justify-center font-bold">
                          {rep.firstName.charAt(0)}{rep.lastName.charAt(0)}
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-white">
                            {rep.firstName} {rep.lastName}
                          </div>
                          {rep.reportsToName && (
                            <div className="text-xs text-gray-400">
                              Reports to: {rep.reportsToName}
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                      {rep.email}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${roleColors[rep.role] || 'bg-gray-600 text-white'}`}>
                        {roleLabels[rep.role] || rep.role}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${statusColors[rep.status] || 'bg-gray-600 text-white'}`}>
                        {statusLabels[rep.status] || rep.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-white">
                      {rep.effectiveRate ? (
                        <span className={rep.rateSource === 'override' ? 'text-keder-yellow' : 'text-gray-300'}>
                          {rep.effectiveRate}%{' '}
                          <span className="text-xs text-gray-500">
                            ({rep.rateSource === 'override' ? 'override' : 'default'})
                          </span>
                        </span>
                      ) : (
                        '-'
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-white">
                      {rep.ordersThisMonth || 0} this month
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-400">
                      {formatDate(rep.hireDate)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleEdit(rep)}
                          className="text-keder-yellow hover:text-yellow-400 font-medium"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleResetPassword(rep)}
                          className="text-blue-400 hover:text-blue-300 font-medium"
                        >
                          Reset Password
                        </button>
                        {rep.status === 'active' && (
                          <button
                            onClick={() => handleDeactivate(rep)}
                            className="text-red-400 hover:text-red-300 font-medium"
                          >
                            Deactivate
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Pagination */}
      {!loading && totalPages > 1 && (
        <div className="card">
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-400">
              Page {page} of {totalPages}
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage(Math.max(1, page - 1))}
                disabled={page === 1}
                className="px-4 py-2 bg-keder-gray text-white rounded-lg hover:bg-keder-gray/80 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <button
                onClick={() => setPage(Math.min(totalPages, page + 1))}
                disabled={page === totalPages}
                className="px-4 py-2 bg-keder-gray text-white rounded-lg hover:bg-keder-gray/80 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          </div>
        </div>
      )}

      {/* User Form Modal */}
      {isModalOpen && (
        <UserFormModal
          rep={editingRep}
          salesReps={salesReps}
          onClose={() => setIsModalOpen(false)}
          onSave={() => {
            setIsModalOpen(false);
            fetchSalesReps();
          }}
        />
      )}

      {isResetPasswordModalOpen && resetPasswordRep && (
        <ResetPasswordModal
          rep={resetPasswordRep}
          onClose={() => {
            setIsResetPasswordModalOpen(false);
            setResetPasswordRep(null);
          }}
        />
      )}
      </>)}
    </div>
  );
}

// Reset Password Modal Component
function ResetPasswordModal({
  rep,
  onClose,
}: {
  rep: DealerSalesRep;
  onClose: () => void;
}) {
  const [newPassword, setNewPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newPassword || newPassword.length < 8) {
      alert('Password must be at least 8 characters');
      return;
    }

    try {
      setLoading(true);
      await dealerSalesRepsApi.resetPassword(rep.id, newPassword);
      alert('Password reset successfully');
      onClose();
    } catch (err: any) {
      alert(`Failed to reset password: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-keder-dark border border-keder-gray rounded-lg p-6 max-w-md w-full">
        <h3 className="text-xl font-bold text-white mb-4">
          Reset Password for {rep.firstName} {rep.lastName}
        </h3>
        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-400 mb-2">
              New Password (min 8 characters)
            </label>
            <input
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              className="input-field"
              placeholder="Enter new password"
              required
              minLength={8}
            />
          </div>
          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 bg-keder-gray text-white rounded-lg hover:bg-keder-gray/80"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 bg-yellow-500 hover:bg-yellow-600 text-black font-semibold rounded-lg disabled:opacity-50"
            >
              {loading ? 'Resetting...' : 'Reset Password'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
